import { createClient } from "@/lib/supabase/client";
import { Box, Button, Flex } from "@radix-ui/themes";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  AtSign,
  Check,
  CheckCircle,
  Lock,
  User,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import styles from "../login/auth.module.css";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<
    "idle" | "checking" | "available" | "taken"
  >("idle");
  const [usernameError, setUsernameError] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLFormElement>(null);
  const usernameCheckTimeout = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const supabase = useMemo(() => createClient(), []);

  // Real-time username validation
  const checkUsernameAvailability = async (username: string) => {
    if (!username || username.length < 1) {
      setUsernameStatus("idle");
      setUsernameError("");
      return;
    }

    if (username.length > 30) {
      setUsernameStatus("taken");
      setUsernameError("Username must be 30 characters or less");
      return;
    }

    setUsernameStatus("checking");
    setUsernameError("");

    try {
      const backendUrl =
        import.meta.env.VITE_PLAYER_SERVICE_URL || "http://localhost:8004";
      const response = await fetch(
        `${backendUrl}/players/check-username/${encodeURIComponent(username)}`,
      );

      if (response.ok) {
        setUsernameStatus("available");
        setUsernameError("");
      } else if (response.status === 409) {
        setUsernameStatus("taken");
        setUsernameError(`Username "${username}" is already taken`);
      } else {
        // Handle other error responses
        console.error("Unexpected response:", response.status);
        setUsernameStatus("idle");
        setUsernameError("Could not verify username. Please try again.");
      }
    } catch (err) {
      console.error("Error checking username:", err);
      setUsernameStatus("idle");
      setUsernameError(
        "Could not connect to server. Please check your connection.",
      );
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);

    // Debounce the username check
    if (usernameCheckTimeout.current) {
      clearTimeout(usernameCheckTimeout.current);
    }

    usernameCheckTimeout.current = setTimeout(() => {
      checkUsernameAvailability(value);
    }, 500);
  };

  const parseSignupError = (errorMessage: string): string => {
    // Parse database trigger errors into user-friendly messages
    if (errorMessage.includes("already taken")) {
      return errorMessage; // Already user-friendly from trigger
    }
    if (errorMessage.includes("already registered")) {
      return "This email is already registered. Try logging in instead.";
    }
    if (errorMessage.includes("invalid email")) {
      return "Please enter a valid email address.";
    }
    if (errorMessage.includes("password")) {
      return "Password must be at least 6 characters long.";
    }
    if (errorMessage.includes("User already registered")) {
      return "This email is already registered. Try logging in instead.";
    }
    // Generic fallback
    return errorMessage;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const backendUrl =
        import.meta.env.VITE_PLAYER_SERVICE_URL || "http://localhost:8004";

      // Pre-flight check: Verify both username and email are available
      const checkResponse = await fetch(
        `${backendUrl}/players/check-availability`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: name, email }),
        },
      );

      if (!checkResponse.ok) {
        const errorData = await checkResponse.json().catch(() => ({}));
        setError(errorData?.detail || "Username or email is already taken");
        return;
      }

      const emailRedirectTo = `${window.location.origin}/auth/callback`;

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: { name, marketing_opt_in: marketingOptIn },
        },
      });

      if (signUpError) {
        setError(parseSignupError(signUpError.message));
        return;
      }

      // Email confirmation required — user exists but no session
      if (data.user && !data.session) {
        setSuccess("Account created, lets go!");
        setName("");
        setEmail("");
        setPassword("");
        setUsernameStatus("idle");
        ref.current?.reset();
        return;
      }

      // User is auto-logged in — show onboarding
      navigate({ to: "/", search: { onboarding: "1" } });
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex align="center" direction="column">
      <form
        ref={ref}
        onSubmit={handleSubmit}
        className={styles.formContainer}
        autoComplete="off"
      >
        <h1 className={styles.title}>
          <span className={styles.neonText}>So, a new</span>
          <span className={styles.neonTextPink}>challenger? Cute.</span>
        </h1>

        {error && (
          <div
            style={{
              color: "#ff0055",
              backgroundColor: "rgba(255, 0, 85, 0.1)",
              padding: "10px",
              borderRadius: "5px",
              marginBottom: "10px",
              border: "1px solid #ff0055",
            }}
          >
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {success && (
          <div
            style={{
              color: "#00ff88",
              backgroundColor: "rgba(0, 255, 136, 0.1)",
              padding: "10px",
              borderRadius: "5px",
              marginBottom: "10px",
              border: "1px solid #00ff88",
            }}
          >
            <CheckCircle size={16} /> {success}
          </div>
        )}

        <div className={styles.socialButtons}>
          <button
            type="button"
            className={`${styles.socialButton} ${styles.discordButton}`}
            onClick={async () => {
              setError("");
              const { error } = await supabase.auth.signInWithOAuth({
                provider: "discord",
                options: {
                  redirectTo: `${window.location.origin}/auth/callback`,
                },
              });
              if (error) setError(error.message);
            }}
          >
            <svg width="20" height="15" viewBox="0 0 71 55" fill="none">
              <path
                d="M60.1 4.9A58.5 58.5 0 0 0 45.4.2a.2.2 0 0 0-.2.1 40.8 40.8 0 0 0-1.8 3.7 54 54 0 0 0-16.2 0A37.4 37.4 0 0 0 25.4.3a.2.2 0 0 0-.2-.1 58.4 58.4 0 0 0-14.7 4.6.2.2 0 0 0-.1.1C1.5 18.7-.9 32 .3 45.1v.2a58.9 58.9 0 0 0 17.8 9 .2.2 0 0 0 .3-.1 42.2 42.2 0 0 0 3.6-5.9.2.2 0 0 0-.1-.3 38.8 38.8 0 0 1-5.5-2.6.2.2 0 0 1 0-.4l1.1-.9a.2.2 0 0 1 .2 0 42 42 0 0 0 35.8 0 .2.2 0 0 1 .2 0l1.1.9a.2.2 0 0 1 0 .4 36.4 36.4 0 0 1-5.5 2.6.2.2 0 0 0-.1.3 47.3 47.3 0 0 0 3.6 5.9.2.2 0 0 0 .3.1A58.7 58.7 0 0 0 70.7 45.3v-.2c1.4-15-2.3-28-9.8-39.6a.2.2 0 0 0-.1-.1ZM23.7 37c-3.4 0-6.3-3.2-6.3-7s2.8-7 6.3-7 6.4 3.1 6.3 7-2.8 7-6.3 7Zm23.3 0c-3.4 0-6.3-3.2-6.3-7s2.8-7 6.3-7 6.4 3.1 6.3 7-2.8 7-6.3 7Z"
                fill="currentColor"
              />
            </svg>
            Sign up with Discord
          </button>
        </div>

        <div className={styles.divider}>
          <span>or</span>
        </div>

        <Box className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="name" className={styles.label}>
              Username
            </label>
            <div className={styles.inputWrapper}>
              <User className={styles.inputIcon} size={18} />
              <input
                id="name"
                type="text"
                name="name"
                autoComplete="nickname"
                data-lpignore="true"
                data-1p-ignore="true"
                data-dashlaneignore="true"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                className={styles.input}
                placeholder="Pick a username. Make it less embarrassing than your last one."
                required
                maxLength={30}
                style={{
                  borderColor:
                    usernameStatus === "taken"
                      ? "#ff0055"
                      : usernameStatus === "available"
                        ? "#00ff88"
                        : undefined,
                }}
              />
              {usernameStatus === "checking" && (
                <span
                  style={{ fontSize: "12px", color: "#888", marginLeft: "8px" }}
                >
                  Checking...
                </span>
              )}
              {usernameStatus === "available" && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#00ff88",
                    marginLeft: "8px",
                  }}
                >
                  <Check size={12} /> Available
                </span>
              )}
            </div>
            {usernameError && (
              <div
                style={{
                  color: "#ff0055",
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                {usernameError}
              </div>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <div className={styles.inputWrapper}>
              <AtSign className={styles.inputIcon} size={18} />
              <input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                data-lpignore="true"
                data-1p-ignore="true"
                data-dashlaneignore="true"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.input}
                placeholder="Give me your email. I promise not to spam… much."
                required
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div className={styles.inputWrapper}>
              <Lock className={styles.inputIcon} size={18} />
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-dashlaneignore="true"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
                placeholder="A strong password. Yes, that means not ‘123456’."
                required
              />
            </div>
          </div>

          <div className={styles.consentSection}>
            <p className={styles.legalText}>
              By signing up, you agree to our{" "}
              <Link to="/terms" className={styles.legalLink}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className={styles.legalLink}>
                Privacy Policy
              </Link>
              .
            </p>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className={styles.checkbox}
              />
              <span className={styles.checkboxText}>
                Keep me updated with product news and feature updates
              </span>
            </label>
          </div>

          <Button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </Box>

        <p className={styles.switchText}>
          Already have an account?{" "}
          <Link to="/login" className={styles.switchLink}>
            Login
          </Link>
        </p>
      </form>
    </Flex>
  );
}
